import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSpaces } from '../contexts/SpacesContext';
import useActivePromos, { findPromo, applyPromo } from '../hooks/useActivePromos';

const AmenityBadge = ({ text }) => (
  <div className="flex items-center gap-1.5 text-sm text-slate-600">
    <svg className="w-4 h-4 text-brand-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
    {text}
  </div>
);

const PricingPill = ({ option, promo }) => {
  const promoPrice = promo ? applyPromo(promo, option.price) : null;
  return (
    <div className={`border rounded-xl p-4 text-center ${promo ? 'bg-brand-50 border-brand-200' : 'bg-gray-50 border-gray-200'}`}>
      <div className="text-xs text-slate-400 mb-1">{option.label}</div>
      {promoPrice !== null ? (
        <div>
          <span className="text-lg text-slate-400 line-through">${option.price}</span>
          <div className="text-2xl font-extrabold text-brand-600">${promoPrice}</div>
        </div>
      ) : (
        <div className="text-2xl font-extrabold text-slate-800">${option.price}</div>
      )}
      <div className="text-xs text-slate-400 mt-0.5">
        {option.type === 'hourly' ? 'per hour' : option.type === 'half-day' ? 'per half day' : option.type === 'daily' ? 'per day' : 'per month'}
      </div>
      {promo && <div className="text-xs text-brand-600 font-medium mt-1">{promo.name}</div>}
    </div>
  );
};

export default function SpacesPage() {
  const spaces = useSpaces();
  const activePromos = useActivePromos();
  const [activeSpace, setActiveSpace] = useState(spaces[0].id);
  const space = spaces.find((s) => s.id === activeSpace);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">Our Spaces</h1>
          <p className="text-gray-300 text-lg max-w-xl mx-auto">
            Purpose-built for productivity. Choose the workspace that fits your needs and budget.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Space selector tabs */}
        <div className="flex gap-2 flex-wrap mb-10 border-b border-gray-200 pb-0">
          {spaces.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSpace(s.id)}
              className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition-colors border-b-2 -mb-px ${
                activeSpace === s.id
                  ? 'border-brand-500 text-brand-600 bg-white'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>

        {/* Space detail */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Left: info */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: space.bgColor }}>
                {space.type === 'room' ? (
                  <svg className="w-6 h-6" style={{ color: space.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" style={{ color: space.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
                  </svg>
                )}
              </div>
              <div>
                <h2 className="text-2xl font-extrabold text-slate-800">{space.name}</h2>
                <p className="text-sm text-slate-400">
                  {space.type === 'room' ? `Seats up to ${space.capacity} people` : `${space.totalSeats} desks available`}
                </p>
              </div>
            </div>

            <p className="text-slate-600 leading-relaxed mb-6">{space.description}</p>

            <h3 className="font-semibold text-slate-700 mb-3">What's included</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
              {(space.amenities || []).map((a) => <AmenityBadge key={a} text={a} />)}
            </div>

            <div className="flex gap-3">
              <Link to={`/book?space=${space.id}`} className="btn-primary">Book This Space</Link>
              <Link to="/availability" className="btn-secondary">Check Availability</Link>
            </div>
          </div>

          {/* Right: pricing */}
          <div>
            <div className="card p-6">
              <h3 className="font-bold text-slate-800 text-lg mb-5">Pricing</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {space.pricingOptions.map((opt) => <PricingPill key={opt.type} option={opt} promo={findPromo(activePromos, space.id, opt.type)} />)}
              </div>

              <div className="bg-brand-50 border border-brand-100 rounded-xl p-4 text-sm text-brand-800">
                <div className="flex gap-2">
                  <svg className="w-4 h-4 mt-0.5 text-brand-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <strong>Membership agreement required.</strong> You will be asked to accept a membership agreement during the booking process before your booking is confirmed.
                  </div>
                </div>
              </div>

              <Link to={`/book?space=${space.id}`} className="btn-primary w-full text-center mt-4 block">
                Book {space.name}
              </Link>
            </div>

            {/* House rules */}
            <div className="card p-6 mt-4">
              <h3 className="font-bold text-slate-800 text-sm mb-3">House Rules</h3>
              <ul className="text-sm text-slate-500 space-y-1.5">
                {[
                  'Please keep noise levels respectful of others',
                  'Clean up shared kitchen and lounge areas',
                  'Meeting rooms must be vacated on time',
                  'Visitors must be registered at reception',
                  'No smoking anywhere on the premises',
                ].map((rule) => (
                  <li key={rule} className="flex items-start gap-2">
                    <span className="text-brand-400 mt-0.5">·</span>
                    {rule}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
